import React from 'react';
import fr from 'facet-rest';
import {
  FDBox, FDIcon, FDLozenge, FDStack,
} from '@facet-wealth/design';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import ListItemRemoveDialog
  from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/ListItemRemoveButton/ListItemRemoveDialog';
import MaterialIconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import { noop } from 'lodash';

const ItemSummary = ({ isFirstItem, isLastItem, onRefresh }) => {
  const theme = useTheme();
  const { resource } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();
  const isMobile = useMobileLayout();
  const { loadModalCollectionByLink } = useModalContext();

  const handleRemove = async (e) => {
    e.stopPropagation();
    try {
      const [resourceToDelete] = await resource.relationship(fr.relationships.removeItem.name).link('related').get();
      await resourceToDelete.delete();
      await onRefresh();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to delete item.');
    }
  };

  const {
    dialogOpen,
    loading,
    handleClose,
    handleSubmit,
    handleOpen,
  } = useConfirmationDialogState({
    onClose: (e) => { e.stopPropagation(); },
    onSubmit: handleRemove,
    onOpen: (e) => { e.stopPropagation(); },
  });

  const isMissingInformation = resource.attr('isMissingInformation');
  const editRel = resource.withRelationship(fr.relationships.edit.name, (rel) => rel, () => null);
  const onItemClick = () => {
    loadModalCollectionByLink(editRel.link('related').url());
  };

  return (
    <Box
      sx={{
        border: `solid 1px ${theme.palette.facet.stone600}`,
        borderTopLeftRadius: isFirstItem ? '16px' : '0px',
        borderTopRightRadius: isFirstItem ? '16px' : '0px',
        borderBottomLeftRadius: isLastItem ? '16px' : '0px',
        borderBottomRightRadius: isLastItem ? '16px' : '0px',
        borderBottomWidth: isLastItem ? '1px' : '0px', // this is so that we don't have a super thick border at the bottom
        '&:hover': {
          backgroundColor: editRel ? `${theme.palette.facet.lightGray}` : '',
          cursor: editRel ? 'pointer' : '',
        },
      }}
      onClick={onItemClick}
      {...dataAttrs('assets-and-liabilities-item-summary')}
    >
      <FDStack direction={'column'} padding={2}>
        <FDStack direction={'row'} justifyContent={'space-between'} spacing={1}>
          <FDStack direction={'row'} spacing={1} alignItems={'center'}>
            {isMissingInformation ? <FDIcon icon={'error'} color={'error'} size={'sm'} {...dataAttrs('missing-info-icon')} /> : null}
            <Typography
              style={{ textDecoration: 'underline', alignSelf: 'center' }}
              variant={'h5'}
              color={isMissingInformation ? 'errorMain' : 'oceana500'}
            >
              {resource.attr('name')}
            </Typography>
          </FDStack>
          <FDStack direction={'row'} spacing={1} alignItems={'center'}>
            <MetaControlField attr={'balance'} />
            {resource.withRelationship(fr.relationships.removeItem.name,
              (rel) => (
                <>
                  <MaterialIconButton
                    {...dataAttrs('remove-item-button')}
                    onClick={handleOpen}
                    aria-label="close"
                    sx={{
                      padding: 0,
                      width: '20px',
                      height: '20px',
                    }}
                    disableRipple
                  >
                    <FDIcon icon={rel.meta('icon')} color={'textDisabled'} size={'sm'} />
                  </MaterialIconButton>
                  <ListItemRemoveDialog
                    handleSubmit={handleSubmit}
                    handleClose={handleClose}
                    loading={loading}
                    dialogOpen={dialogOpen}
                    dialogProps={{
                      cancelLabel: 'Cancel',
                      confirmLabel: 'Remove',
                    }}
                  />
                </>
              ),
              () => null)}
          </FDStack>
        </FDStack>
        <FDStack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'flex-start' : 'center'}>
          <FDBox width={'300px'}>
            <MetaControlField attr={'description'} simpleRootStyle />
          </FDBox>
          <FDStack direction={'row'} spacing={1} alignItems={'flex-start'}>
            {resource.attr('infoSnippets')?.resources().list().map((infoSnippet) => (
              <FDLozenge
                label={infoSnippet.attr('name')}
                color={infoSnippet.attr('status')}
                alignSelf={'flex-end'}
              />
            ))}
          </FDStack>
        </FDStack>
      </FDStack>
    </Box>
  );
};

ItemSummary.defaultProps = {
  isFirstItem: false,
  isLastItem: false,
  onRefresh: noop,
};

ItemSummary.propTypes = {
  isFirstItem: PropTypes.bool,
  isLastItem: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export { ItemSummary };
